import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Trash2, Eye, EyeOff, Printer, ChevronRight, FilePlus, Plus } from "lucide-react";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import ConfirmModal from "../ConfirmModal";
import PhaseChip from "../PhaseChip";
import JobCardRow from "../JobCardRow";
import JobCardPrintModal from "./JobCardPrintModal";
import { JOBCARD_PHASES } from "../../lib/enums";

/**
 * JobCardsBlock — list of jobcards under a customer.
 *
 * Per the customer-profile rule, every row is a <Link to=/jobcards/:id>.
 * The previous half-info modal has been removed entirely. Inline icon
 * buttons (print / hide-from-client / delete) live next to each row so
 * power users don't have to navigate first.
 */
const PHASE_ORDER = JOBCARD_PHASES.map((p) => p.key);

export default function JobCardsBlock({ jobs, sites, canEdit, onChanged, defaultOpen = false, previewEditMode = false, isPortal = false, isPreview = false, customerId = null, customerName = "" }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [printJob, setPrintJob] = useState(null);
  // Wave 26.2 — JC clicks in portal mode (real customer OR owner
  // preview) navigate to the full-page /portal/jobcards/:id route.
  // Tiny popups are explicitly forbidden by the design contract; the
  // customer must get the same scrollable detail experience as staff,
  // just with internal fields filtered server-side.

  const isClosed = (p) => ["closed", "cancelled", "paid"].includes(p);
  const visible = showClosed ? jobs : jobs.filter((j) => !isClosed(j.phase));
  const grouped = {};
  visible.forEach((j) => {
    const k = j.phase || "created";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(j);
  });
  const orderedPhases = Object.keys(grouped).sort(
    (a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b),
  );
  const siteOf = (sid) => sites.find((s) => s.id === sid);

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/jobcards/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally { setBusy(false); }
  };

  const toggleHide = async (job) => {
    setBusy(true);
    try {
      await api.put(`/jobcards/${job.id}`, {
        ...job,
        hidden_from_client: !job.hidden_from_client,
      });
      onChanged?.();
    } finally { setBusy(false); }
  };

  const closedCount = jobs.filter((j) => isClosed(j.phase)).length;

  return (
    <FoldBlock
      icon={Wrench}
      iconColor="#f59e0b"
      title="Job cards"
      count={visible.length}
      defaultOpen={defaultOpen}
      navigateTo={isPortal ? undefined : "/jobcards"}
      navigateLabel="Job cards page"
      testId="block-jobcards"
      emptyHint={showClosed ? "No job cards." : "No active job cards. Toggle 'Show closed' to see archived."}
      headerExtra={
        closedCount > 0 && (
          <label
            className="text-[10px] uppercase tracking-wider text-[#647087] flex items-center gap-1 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="jobcards-show-closed" />
            +{closedCount} closed
          </label>
        )
      }
    >
      {/* Iter 413 (Dirk, 2026-02-28) — Customer-side template shortcuts.
          "Use template" opens a template picker filtered to this
          customer + globals; "Create template for this customer"
          jumps into the Templates screen pinned to this customer.
          Templates only create tasks — the details (tech, staff,
          vehicle, dates, instructions) still come from the normal
          New Job Card flow. */}
      {canEdit && customerId && !isPortal && (
        <CustomerJcTemplateShortcuts customerId={customerId} customerName={customerName} />
      )}
      <div className="space-y-2">
        {orderedPhases.map((phase) => (
          <PhaseGroup
            key={phase}
            phase={phase}
            items={grouped[phase]}
            siteOf={siteOf}
            canEdit={canEdit}
            previewEditMode={previewEditMode}
            isPortal={isPortal}
            isPreview={isPreview}
            customerId={customerId}
            onPrint={(j) => setPrintJob(j)}
            onToggleHide={toggleHide}
            onDelete={(j) => setConfirmDel(j)}
          />
        ))}
      </div>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete job card"
        message={
          <>Permanently delete job card{" "}
            <b>{confirmDel?.short_id ? `#${confirmDel.short_id} ` : ""}{confirmDel?.title}</b>?</>
        }
        confirmLabel="Delete job card"
        testId="jobcard-delete-confirm"
        busy={busy}
      />

      {printJob && (
        <JobCardPrintModal
          job={printJob}
          customerName={customerName}
          onClose={() => setPrintJob(null)}
        />
      )}
    </FoldBlock>
  );
}

function CustomerJcTemplateShortcuts({ customerId, customerName }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  // Prefetch count once so the "(N)" chip is accurate. The full picker
  // still re-loads on toggle in case a template was just created.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [scoped, globals] = await Promise.all([
          api.get(`/jobcard-templates?customer_id=${customerId}`).catch(() => ({ data: [] })),
          api.get(`/jobcard-templates?customer_id=__global__`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const merged = [...(scoped.data || []), ...(globals.data || [])];
        const seen = new Set();
        const out = [];
        for (const t of merged) {
          if (!seen.has(t.id)) { seen.add(t.id); out.push(t); }
        }
        setTemplates(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  return (
    <div className="mb-3 flex items-center gap-2 flex-wrap"
         data-testid={`jc-templates-shortcuts-${customerId}`}>
      <button
        type="button"
        onClick={() => setShowPicker((v) => !v)}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid={`jc-templates-use-btn-${customerId}`}
      >
        <FilePlus className="w-3 h-3" />
        Use template
        {templates.length > 0 && (
          <span className="ml-1 text-[9px] font-mono text-[#647087]">
            ({templates.length})
          </span>
        )}
      </button>
      <Link
        to={`/jobcards/templates/new?customer_id=${customerId}`}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid={`jc-templates-new-for-customer-${customerId}`}
      >
        <Plus className="w-3 h-3" />
        Create template for this customer
      </Link>
      <Link
        to={`/jobcards/templates?customer_id=${customerId}`}
        className="text-[10px] text-[#647087] hover:text-[#1FB6C1] underline ml-auto"
        data-testid={`jc-templates-manage-${customerId}`}
      >
        Manage all templates →
      </Link>

      {showPicker && (
        <div className="w-full mt-1 hv-card p-2 space-y-1">
          {loading && (
            <div className="text-[11px] text-[#647087] text-center py-2">
              Loading…
            </div>
          )}
          {!loading && templates.length === 0 && (
            <div className="text-[11px] text-[#a0aabf] text-center py-2">
              No templates yet for {customerName || "this customer"}.
              <Link to={`/jobcards/templates/new?customer_id=${customerId}`}
                    className="underline text-[#1FB6C1] ml-1">
                Create one
              </Link>.
            </div>
          )}
          {!loading && templates.map((t) => (
            <Link
              key={t.id}
              to={`/jobcards/new?template_id=${t.id}&customer_id=${customerId}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/[0.03] transition"
              data-testid={`jc-templates-pick-${customerId}-${t.id}`}
            >
              <FilePlus className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{t.name}</div>
                <div className="text-[10px] text-[#647087]">
                  {t.customer_id ? "Customer-specific" : "Global"} · {t.task_count} tasks
                </div>
              </div>
              <ChevronRight className="w-3 h-3 text-[#647087]" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}


function PhaseGroup({ phase, items, siteOf, canEdit, previewEditMode, isPortal, isPreview, customerId, onPrint, onToggleHide, onDelete }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-white/5 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.03] transition-colors"
        data-testid={`jobcards-group-${phase}`}
      >
        <div className="flex items-center gap-2">
          <ChevronRight className={`w-3 h-3 text-[#647087] transition-transform ${open ? "rotate-90" : ""}`} />
          <PhaseChip value={phase} />
          <span className="text-xs text-[#647087]">{items.length} {items.length === 1 ? "job" : "jobs"}</span>
        </div>
      </button>
      {open && (
        <div className="space-y-1 p-1">
          {items.map((j) => (
            <JobCardRow
              key={j.id}
              job={j}
              site={siteOf(j.site_id)}
              // SECURITY-CRITICAL — portal mode (real customer OR owner
              // preview) navigates to /portal/jobcards/:id which fetches
              // the scrubbed JC. Never link to /jobcards/:id (staff page
              // would leak invoice notes / costs / debrief).
              to={
                isPortal
                  ? `/portal/jobcards/${j.id}${customerId ? `?customer_id=${customerId}` : ""}`
                  : `/jobcards/${j.id}`
              }
              showPhaseChip={false}
              showCustomer={false}
              testIdPrefix="jobcards-row"
              actions={!isPortal ? (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onPrint(j); }}
                    className="hv-btn hv-btn-ghost text-xs px-2 py-1"
                    title="Print / PDF"
                    data-testid={`jobcards-row-print-${j.id}`}
                  >
                    <Printer className="w-3.5 h-3.5" />
                  </button>
                  {previewEditMode && canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onToggleHide(j); }}
                      className={`hv-btn hv-btn-ghost text-xs px-2 py-1 ${j.hidden_from_client ? "text-emerald-300" : "text-amber-300"}`}
                      title={j.hidden_from_client ? "Show to client" : "Hide from client"}
                      data-testid={`jobcards-row-hide-${j.id}`}
                    >
                      {j.hidden_from_client ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onDelete(j); }}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-rose-300"
                      title="Delete"
                      data-testid={`jobcards-row-delete-${j.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              ) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
